import { FontAwesome } from '@expo/vector-icons';
import React, { useState } from 'react';
import {
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { TextInput } from 'react-native-gesture-handler';
import Colors from '../../constants/Colors';
import DeleteIcon16 from '../16pxIcons/DeleteIcon16';
import CloseModalIcon from '../CloseModalIcon';
import Style from '../../constants/Style';
import editUser from '../../requests/Users/EditUser';
import AlertModal from '../AlertModal';
import { logEvent } from '../LogEvent';
import { OOPS_SOMETHING_WENT_WRONG } from '../../constants/AlertMessages';
import { PressableState } from '../PressableStyled';

export default function EditUserMobile(props) {
  const [roleDropdownVisible, setRoleDropdownVisible] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedRoleName, setSelectedRoleName] = useState(null);
  const [tags, setTags] = useState([]);
  const [tagsUpdated, setTagsUpdated] = useState(false);
  const [alertVisible, setAlertVisible] = useState(false);
  const [alertError, setAlertError] = useState(true);
  const [alertTitle, setAlertTitle] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const showTags = false;

  const getTestID = (role) => {
    switch (role) {
      case 'Admin':
        return 'um-admin-role-btn';
      case 'Non Admin':
        return 'um-non-admin-role-btn';
      default:
        return 'um-other-role-btn';
    }
  };

  const RenderSaveCancel = () => (
    <View
      style={{
        flexDirection: 'row',
        marginVertical: 30,
      }}
    >
      <TouchableOpacity
        style={{
          backgroundColor: Colors.light.hereworksLightBlue,
          borderRadius: 4,
          marginRight: 12,
          opacity: isSaving ? 0.7 : 1,
        }}
        disabled={isSaving}
        onPress={async () => {
          if (isSaving) return;
          logEvent('EditUser', 'Save');
          setIsSaving(true);
          try {
            const res = await editUser(
              props.user.email,
              props.user.firstName,
              props.user.lastName,
              selectedRole === 'Admin',
              selectedRole === 'Admin' ? null : selectedRole,
            );
            if (res?.isError) {
              setAlertTitle(OOPS_SOMETHING_WENT_WRONG);
              setAlertError(true);
            } else {
              setAlertTitle(res?.message ?? 'User edited successfully!');
              setAlertError(false);
              props.roleChanged();
            }
          } catch (error) {
            setAlertTitle(error?.message || OOPS_SOMETHING_WENT_WRONG);
            setAlertError(true);
          } finally {
            setAlertVisible(true);
            setIsSaving(false);
          }
        }}
      >
        <View style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          marginVertical: 8,
          marginHorizontal: 24,
        }}>
          {isSaving && (
            <ActivityIndicator
              size='small'
              color='#fff'
              style={{ marginRight: 8 }}
            />
          )}
          <Text
            style={{
              color: '#fff',
              fontFamily: 'Roboto_400Regular',
              fontSize: 16,
            }}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Text>
        </View>
      </TouchableOpacity>
      <TouchableOpacity
        style={{
          backgroundColor: '#fff',
          borderRadius: 4,
          borderWidth: 1,
          borderColor: Colors.light.hereworksLightBlue,
          opacity: isSaving ? 0.6 : 1,
        }}
        disabled={isSaving}
        onPress={() => {
          logEvent('EditUser', 'Cancel');
          props.setEditModalVisible(false);
        }}
      >
        <Text
          style={{
            color: Colors.light.hereworksLightBlue,
            fontFamily: 'Roboto_400Regular',
            fontSize: 16,
            marginVertical: 8,
            marginHorizontal: 24,
          }}
        >
          Cancel
        </Text>
      </TouchableOpacity>
    </View>
  );

  const renderTag = (title: string) => (
    <TouchableOpacity
      style={{
        backgroundColor: Colors.light.hereworksYellow50,
        borderColor: Colors.light.hereworksDarkYellow700,
        borderWidth: 1,
        borderRadius: 4,
        marginRight: 10,
        marginBottom: 10,
        flexDirection: 'row',
        alignItems: 'center',
      }}
      onPress={() => {
        setTags(tags.filter((tag) => tag !== title));
        setTagsUpdated(!tagsUpdated);
        logEvent('EditUser', 'Tag');
      }}
    >
      <Text
        style={{
          color: Colors.light.hereworksDarkYellow700,
          fontFamily: 'Roboto_500Medium',
          fontSize: 12,
          lineHeight: 14,
          marginLeft: 8,
          marginVertical: 5.5,
        }}
      >
        {title}
      </Text>
      <DeleteIcon16
        color={Colors.light.hereworksDarkYellow700}
        style={{ marginHorizontal: 4 }}
      />
    </TouchableOpacity>
  );

  return (
    <View
      style={{ alignItems: 'center' }}
    >
      <Pressable
        style={{
          flex: 1,
          backgroundColor: '#00000020',
          justifyContent: 'flex-end',
          alignItems: 'center',
          cursor: 'auto',
        }}
        onPress={() => {
          props.setEditModalVisible(false);
          logEvent('EditUser', 'Close_Modal');
        }}
      >
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'position' : 'height'}
        >
          <View
            style={{
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 10,
              backgroundColor: '#fff',
            }}
          >
            <Pressable
              onPress={() => {
                if (isSaving) return;
                logEvent('EditUser', 'Close_Modal');
                props.setEditModalVisible(false);
              }}
              style={({ hovered }: PressableState) => [
                Style.closeButton,
                {
                  backgroundColor: hovered
                    ? Colors.light.hereworksBlue200
                    : Colors.light.hereworksBlue50,
                },
              ]}
              testID="um-close-edit-user-modal-btn"
            >
              <CloseModalIcon />
            </Pressable>
            <Text
              style={{
                fontFamily: 'Poppins_600SemiBold',
                fontSize: 20,
                marginBottom: 15,
                marginTop: 24,
                paddingHorizontal: 71,
                textAlign: 'center',
              }}
            >
              Give a role to the member
            </Text>
            <Text
              style={{
                fontSize: props.mobile ? 16 : 20,
                fontFamily: 'Poppins_500Medium',
                color: Colors.light.hereworksBlack600,
                marginTop: 4,
                marginBottom: 16,
              }}
            >
              {`${props.user.firstName ? props.user.firstName : props.user.email} ${
                props.user.lastName ?? ''
              }`}
            </Text>
            <View
              style={{
                alignSelf: 'stretch',
                marginHorizontal: 16,
              }}
            >
              <Text style={styles.smallHeader}>Role</Text>
              <TouchableOpacity
                style={styles.dropdownBox}
                onPress={() => {
                  setRoleDropdownVisible(!roleDropdownVisible);
                  logEvent(
                    'EditUser',
                    `Role_dropdown_${roleDropdownVisible ? 'Close' : 'Open'}`,
                  );
                }}
              >
                <Text style={styles.dropdownBoxText}>
                  {selectedRoleName ?? 'Choose role'}
                </Text>
                <View style={{ flex: 1, alignItems: 'flex-end' }}>
                  <FontAwesome
                    size={20}
                    style={{
                      color: Colors.light.hereworksLightBlue,
                      marginRight: 12,
                    }}
                    name={roleDropdownVisible ? 'angle-up' : 'angle-down'}
                  />
                </View>
              </TouchableOpacity>
              {roleDropdownVisible && (
                <>
                  <ScrollView
                    style={styles.dropdownView}
                    nestedScrollEnabled
                    keyboardShouldPersistTaps='handled'
                  >
                    <Pressable
                      key={0}
                      onPress={() => {
                        setSelectedRole('Admin');
                        setSelectedRoleName('Admin');
                        setRoleDropdownVisible(!roleDropdownVisible);
                        logEvent('EditUser', 'Selected_group');
                      }}
                      testID={getTestID('Admin')}
                    >
                      <Text style={styles.dropdownItemText}>Admin</Text>
                    </Pressable>
                    {props.roles.map((role, key) => (
                      <Pressable
                        key={key}
                        onPress={() => {
                          setSelectedRole(role.id);
                          setSelectedRoleName(role.name);
                          setRoleDropdownVisible(!roleDropdownVisible);
                          logEvent('EditUser', 'Selected_group');
                        }}
                        testID={getTestID(role.name)}
                      >
                        <Text style={styles.dropdownItemText}>{role.name}</Text>
                      </Pressable>
                    ))}
                  </ScrollView>
                </>
              )}
              {showTags && (
                <>
                  <Text style={styles.smallHeader}>Add tag</Text>
                  <TextInput
                    style={styles.noteText}
                    placeholderTextColor={Colors.light.hereworksBlack400}
                    placeholder={'Enter tag'}
                    onSubmitEditing={(text) => {
                      const exTags = tags;
                      exTags.push(text.nativeEvent.text);
                      setTags(exTags);
                      setTagsUpdated(!tagsUpdated);
                    }}
                  />
                  <View
                    style={{
                      flexWrap: 'wrap',
                      flexDirection: 'row',
                      marginTop: 7,
                    }}
                  >
                    {tags.map((tag, key) => (
                      <View key={key}>{renderTag(tag)}</View>
                    ))}
                  </View>
                </>
              )}
            </View>
            <RenderSaveCancel />
          </View>
        </KeyboardAvoidingView>
      </Pressable>
      <AlertModal
        modalVisible={alertVisible}
        error={alertError}
        title={alertTitle}
        heading='Edit User'
        setModalVisible={setAlertVisible}
        action={() => props.setEditModalVisible(false)}
        cancelAction={() => props.onClose()}
        mobileFullScreen={true}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  noteText: {
    color: Colors.light.hereworksPrimaryGrey,
    alignSelf: 'stretch',
    fontFamily: 'Roboto_400Regular',
    alignItems: 'center',
    fontSize: 16,
    backgroundColor: Colors.light.hereworksWhite200,
    alignContent: 'center',
    marginVertical: 5,
    paddingVertical: 10,
    paddingHorizontal: 8,
    borderRadius: 6,
    verticalAlign: 'top',
    outlineWidth: 0,
  },
  smallHeader: {
    fontSize: 16,
    lineHeight: 24,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
    alignSelf: 'flex-start',
  },
  dropdownBox: {
    borderWidth: 1.5,
    borderColor: Colors.light.hereworksPaleBlue900,
    borderRadius: 10,
    marginTop: 5,
    alignSelf: 'stretch',
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    zIndex: 1,
  },
  dropdownView: {
    width: '100%',
    marginTop: 5,
    zIndex: 5,
    borderRadius: 10,
    borderWidth: 1.5,
    borderColor: Colors.light.hereworksPaleBlue900,
    maxHeight: 150,
    backgroundColor: '#fff',
  },
  dropdownItemText: {
    marginLeft: 12,
    marginVertical: 9,
    fontFamily: 'Poppins_400Regular',
    fontSize: 14,
    color: Colors.light.hereworksPrimaryGrey,
  },
  dropdownBoxText: {
    fontFamily: 'Poppins_400Regular',
    fontSize: 14,
    lineHeight: 21,
    color: Colors.light.hereworksPrimaryGrey,
    marginVertical: 9.5,
    marginLeft: 12,
  },
});
